import { HStack, Text, VStack } from '@ayble-health/dashboard-components';
import React, { useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { MeetingNote, ProviderType } from '../../../api/types';
import { useUserBlocks } from '../../../api/healthCoach/useUserBlocks';
import { useUserMeetings } from '../../../api/healthCoach/useUserMeetings';
import { BLOCK_TYPE, UserBlock } from '../../../models/block';
import type { CareModel } from '../../../models/user';
import { Loader } from '../../Loader';
import { formatDate, formatDateInTZ } from '../utils';
import CalendarSVG from '../../CustomSVGs/CalendarSVG';
import { computeNextMeetings } from '../../AppointmentsPage/utils';
import { MeetingBlockCard } from './MeetingBlockCard';
import { VisitBlockCard } from './VisitBlockCard';
import { useDashboardPageState } from '../../../hooks/useDashboardPageState';
import {
  getVisitBlockProviderLabel,
  getVisitBlockProviderType,
} from '../../../utils/visitBlockProvider';

type VisitListItem = {
  id: string;
  clientConfirmed?: boolean;
  date: string;
  providerLabel: string;
  providerType?: ProviderType;
  requestedTimeframe?: string | null;
  type: 'meeting' | 'visitBlock';
};

const getVisitBlockListItem = (
  visit: UserBlock,
  careModel?: CareModel | null
): VisitListItem => {
  return {
    id: `block-${visit.id}`,
    date: visit.date_assigned ?? '',
    providerLabel: getVisitBlockProviderLabel(visit.stable_string, careModel),
    providerType: getVisitBlockProviderType(visit.stable_string, careModel),
    requestedTimeframe: visit.instructions,
    type: 'visitBlock',
  };
};

const getMeetingListItem = (meeting: MeetingNote): VisitListItem => {
  return {
    id: `meeting-${meeting.healthie_appointment_id}`,
    clientConfirmed: meeting.client_confirmed,
    date: meeting.meeting_date,
    providerLabel:
      meeting.provider?.display_name || meeting.provider?.full_name || '',
    providerType: meeting.provider?.type ?? ProviderType.Coach,
    type: 'meeting',
  };
};

type VisitBlockProps = {
  onOpenAppointments: (providerType?: ProviderType) => void;
};

export const VisitBlock = ({ onOpenAppointments }: VisitBlockProps) => {
  const { patient } = useParams();
  const {
    state: { user },
  } = useDashboardPageState();
  const { blocks: visitBlocks, isLoading } = useUserBlocks({
    archived: false,
    completed: false,
    blockType: BLOCK_TYPE.Visit,
  });
  const {
    meetings,
    isLoading: isMeetingsLoading,
  } = useUserMeetings(patient ?? '');

  const upcomingVisits = useMemo<VisitListItem[]>(() => {
    const activeVisitBlocks = visitBlocks.map((visitBlock) =>
      getVisitBlockListItem(visitBlock, user?.care_model)
    );

    const nextMeetings = computeNextMeetings(meetings ?? [])
      .map(getMeetingListItem)
      .sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );

    const sortedVisitBlocks = [...activeVisitBlocks].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    return [...nextMeetings, ...sortedVisitBlocks];
  }, [meetings, user?.care_model, visitBlocks]);

  return (
    <VStack
      rounded={'lg'}
      p={4}
      borderWidth={1}
      borderColor={'cloud.200'}
      alignItems={'flex-start'}
      gap={3}
      flexGrow={2}
      h={'100%'}
    >
      <HStack
        alignItems={'center'}
        borderBottomWidth={1}
        borderColor={'cloud.200'}
        gap={2}
        mx={-4}
        px={4}
        pb={3}
        w={'calc(100% + 32px)'}
      >
        <CalendarSVG />
        <Text color={'seafoam'} fontSize={'sm'} fontWeight={'semibold'}>
          Next visits
        </Text>
      </HStack>

      {isLoading || isMeetingsLoading ? (
        <Loader loading />
      ) : !upcomingVisits.length ? (
        <Text color={'blueGray.600'} fontSize={'sm'} fontStyle={'italic'}>
          No upcoming visits.
        </Text>
      ) : (
        <VStack gap={0} w={'100%'}>
          {upcomingVisits.map((visit, index) => {
            const isLastCard = index === upcomingVisits.length - 1;
            const cardProps = {
              providerLabel: visit.providerLabel,
              onPress: () =>
                onOpenAppointments(
                  visit.type === 'visitBlock' ? visit.providerType : undefined
                ),
            };

            return (
              <Box
                key={visit.id}
                borderBottomWidth={isLastCard ? 0 : 1}
                borderColor={'cloud.200'}
                mx={-4}
                pb={isLastCard ? 0 : 3}
                pt={index === 0 ? 0 : 3}
                px={4}
                w={'calc(100% + 32px)'}
              >
                {visit.type === 'meeting' ? (
                  <MeetingBlockCard
                    {...cardProps}
                    clientConfirmed={!!visit.clientConfirmed}
                    date={formatDateInTZ(
                      visit.date || undefined,
                      user?.timezone,
                      'M/D/YYYY'
                    )}
                    time={formatDateInTZ(
                      visit.date || undefined,
                      user?.timezone,
                      'h:mm A z'
                    )}
                  />
                ) : (
                  <VisitBlockCard
                    {...cardProps}
                    date={formatDate(visit.date || undefined, 'M/D/YYYY')}
                    requestedTimeframe={visit.requestedTimeframe}
                  />
                )}
              </Box>
            );
          })}
        </VStack>
      )}
    </VStack>
  );
};

export default VisitBlock;
